/**
 * Quick-add gallery navigation arrows.
 *
 * Lives OUTSIDE #quick-add-modal-content so morph() never destroys it.
 * Observes that container via MutationObserver; when gallery markup appears
 * it injects the theme's <slideshow-arrows> (the same markup the PDP gallery
 * renders) into `media-gallery` and wires scroll-based disabled state +
 * click-to-scroll behaviour.
 *
 * The slideshow's own <slideshow-arrows> is also in the cloned content but
 * stays hidden (quick-add-modal-styles): its buttons drive slideshow-component
 * paging, which jumps by "visible slides" and loops, whereas the drawer strip
 * steps one image at a time and stops at each end.
 */
class QuickAddGalleryNav extends HTMLElement {
  /** @type {MutationObserver|null} */
  #observer = null;

  /** @type {number} */
  #debounceTimer = 0;

  /** @type {HTMLElement|null} */
  #scrollContainer = null;

  /** @type {HTMLElement|null} */
  #arrows = null;

  /** @type {HTMLButtonElement|null} */
  #prevBtn = null;

  /** @type {HTMLButtonElement|null} */
  #nextBtn = null;

  /** @type {(() => void)|null} */
  #boundScrollHandler = null;

  /** @type {(() => void)|null} */
  #boundPrevClick = null;

  /** @type {(() => void)|null} */
  #boundNextClick = null;

  /** Pixels to scroll per click — 240px item + 4px gap */
  static SCROLL_STEP = 244;

  connectedCallback() {
    const targetId = this.dataset.target;
    if (!targetId) return;

    const target = document.getElementById(targetId);
    if (!target) return;

    this.#observer = new MutationObserver(() => {
      clearTimeout(this.#debounceTimer);
      this.#debounceTimer = window.setTimeout(() => this.#init(), 50);
    });

    this.#observer.observe(target, { childList: true, subtree: true });
  }

  disconnectedCallback() {
    this.#cleanup();
    if (this.#observer) {
      this.#observer.disconnect();
      this.#observer = null;
    }
    clearTimeout(this.#debounceTimer);
  }

  /**
   * Find the visible scrollable gallery container and inject arrows.
   * Called on every meaningful DOM mutation inside the target (debounced).
   */
  #init() {
    const targetId = this.dataset.target;
    if (!targetId) return;

    const content = document.getElementById(targetId);
    if (!content) return;

    /* Inside media-gallery so the PDP's arrow rules
       (.product-information__media media-gallery slideshow-arrows …) match. */
    const gallery = content.querySelector('.product-information__media media-gallery');
    if (!gallery) return;

    const scrollContainer = this.#findScrollContainer(content);
    if (!scrollContainer) {
      /* No multi-image gallery — remove any stale arrows */
      this.#cleanup();
      return;
    }

    /* If the same container is already wired, skip re-init */
    if (this.#scrollContainer === scrollContainer && this.#arrows?.isConnected) return;

    this.#cleanup();

    const template = this.querySelector('template');
    if (!template) return;

    const fragment = template.content.cloneNode(true);
    const arrows = fragment.querySelector('slideshow-arrows');
    const prevBtn = fragment.querySelector('.slideshow-control--previous');
    const nextBtn = fragment.querySelector('.slideshow-control--next');

    if (!(arrows instanceof HTMLElement)) return;
    if (!(prevBtn instanceof HTMLButtonElement) || !(nextBtn instanceof HTMLButtonElement)) return;

    /* The slideshow-arrow snippet targets a parent slideshow-component through
       `on:click="/previous"` and `ref`. Drop both so the clicks are ours alone;
       component.js reads `on:*` at dispatch time, so removing the attribute unbinds it. */
    for (const button of [prevBtn, nextBtn]) {
      button.removeAttribute('on:click');
      button.removeAttribute('ref');
    }

    this.#arrows = arrows;
    this.#prevBtn = prevBtn;
    this.#nextBtn = nextBtn;
    this.#scrollContainer = scrollContainer;

    gallery.appendChild(arrows);

    /* Bind events */
    this.#boundScrollHandler = () => this.#updateArrowState();
    this.#boundPrevClick = () => this.#scroll(-1);
    this.#boundNextClick = () => this.#scroll(1);

    this.#scrollContainer.addEventListener('scroll', this.#boundScrollHandler, { passive: true });
    this.#prevBtn.addEventListener('click', this.#boundPrevClick);
    this.#nextBtn.addEventListener('click', this.#boundNextClick);

    /* Initial state */
    this.#updateArrowState();
  }

  /**
   * Locate the visible scrollable container with more than one child.
   * Desktop grid mode uses `.media-gallery__grid`; mobile/carousel uses
   * `slideshow-slides`.
   *
   * @param {HTMLElement} content
   * @returns {HTMLElement|null}
   */
  #findScrollContainer(content) {
    const candidates = [
      content.querySelector('.media-gallery__grid'),
      content.querySelector('slideshow-slides'),
    ];

    for (const el of candidates) {
      if (!el) continue;
      const style = getComputedStyle(el);
      if (style.display === 'none') continue;
      if (el.children.length <= 1) continue;
      return /** @type {HTMLElement} */ (el);
    }

    return null;
  }

  /**
   * Disable the prev arrow at the start of the strip and the next arrow at the end.
   */
  #updateArrowState() {
    if (!this.#scrollContainer || !this.#prevBtn || !this.#nextBtn) return;

    const { scrollLeft, scrollWidth, clientWidth } = this.#scrollContainer;
    const atStart = scrollLeft <= 1;
    const atEnd = scrollLeft + clientWidth >= scrollWidth - 1;

    this.#prevBtn.disabled = atStart;
    this.#nextBtn.disabled = atEnd;
  }

  /**
   * Scroll by one item width.
   * @param {-1|1} direction
   */
  #scroll(direction) {
    if (!this.#scrollContainer) return;
    this.#scrollContainer.scrollBy({
      left: direction * QuickAddGalleryNav.SCROLL_STEP,
      behavior: 'smooth',
    });
  }

  /**
   * Remove injected arrows and unbind listeners.
   */
  #cleanup() {
    if (this.#scrollContainer && this.#boundScrollHandler) {
      this.#scrollContainer.removeEventListener('scroll', this.#boundScrollHandler);
    }
    if (this.#prevBtn && this.#boundPrevClick) {
      this.#prevBtn.removeEventListener('click', this.#boundPrevClick);
    }
    if (this.#nextBtn && this.#boundNextClick) {
      this.#nextBtn.removeEventListener('click', this.#boundNextClick);
    }
    this.#arrows?.remove();
    this.#arrows = null;
    this.#prevBtn = null;
    this.#nextBtn = null;
    this.#scrollContainer = null;
    this.#boundScrollHandler = null;
    this.#boundPrevClick = null;
    this.#boundNextClick = null;
  }
}

if (!customElements.get('quick-add-gallery-nav')) {
  customElements.define('quick-add-gallery-nav', QuickAddGalleryNav);
}
